import { RubyLoader } from '../../components/RubyLoader';
import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, Bell, Calendar, CheckCircle,
  ClipboardList, Clock, Copy, Download, Edit2, Eye, File, FileText, Image,
  Key, Mail, Phone, Plus, RotateCcw, Send, Shield,
  Trash2, Upload, User as UserIcon, UserCheck, UserX, X, XCircle,
} from 'lucide-react';
import { StaffPage } from '../../components/staff/StaffChrome';
import { staffApi, StaffMember, StaffRole, StaffStatus } from '../../services/staffApi';
import {
  staffDocumentTemplates,
  staffOnboardingTemplates,
  staffDocumentRequests,
  staffMemberDocuments,
  staffDocumentUploads,
} from '../../services/staffDocumentService';
import EmployeeProgressPanel from '../../components/staff/EmployeeProgressPanel';
import AssignWorkflowModal from '../../components/staff/AssignWorkflowModal';
import SearchableDropdown from '../../components/SearchableDropdown';
import { DatePicker } from '../../components/DatePicker';
import { toastService } from '../../services/toastService';

/**
 * STAFF / DETAIL — per-staff-member record for RubyTill INTERNAL staff.
 * Ported from platform-app pages/UserDetail.tsx, re-based onto staffApi
 * (staff-service on the shared `platform` cluster) + staffDocumentService.
 *
 * Kept from the source: profile edit, role assignment, status actions
 * (suspend / terminate / reactivate), reset password, resend invite, delete,
 * assigned-workflow progress (EmployeeProgressPanel), assign/remove workflow,
 * document reminder / re-request, and admin-uploaded standalone documents.
 *
 * Dropped: POS access / PIN / locations, commissions, email preferences,
 * training, scheduling, exports, and everything tenant/package related.
 *
 * Guard rails (cannot suspend/terminate/delete yourself or the protected
 * owner) are enforced by the backend — errors surface here as toasts.
 */

// ---------------------------------------------------------------------------
// Local types — response conventions match platform-app documentService.
// ---------------------------------------------------------------------------

interface StaffDocRequest {
  id: string;
  templateName?: string;
  templateType?: string;
  category?: string;
  documentTemplateId?: string;
  onboardingTemplateId?: string;
  status: 'pending' | 'submitted' | 'verified' | 'rejected' | string;
  rejectionReason?: string;
  verifiedAt?: number;
  submittedAt?: number;
  dueDate?: number;
}

interface StaffDocumentRecord {
  id: string;
  requestId?: string;
  fileKey: string;
  fileName: string;
  fileType?: string;
  fileSize?: number;
  documentName?: string;
  uploadedAt?: number;
  expiryDate?: number;
}

interface WorkflowSummary {
  id?: string;
  templateId?: string;
  name?: string;
  statusCounts?: { pending?: number; submitted?: number; verified?: number; rejected?: number };
}

interface OnboardingStatusShape {
  totalDocuments?: number;
  progress?: number;
  statusCounts?: { pending?: number; submitted?: number; verified?: number; rejected?: number };
  workflows?: WorkflowSummary[];
  activeWorkflowIds?: string[];
}

// ---------------------------------------------------------------------------
// Small display helpers
// ---------------------------------------------------------------------------

const formatFileSize = (bytes?: number): string => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (v?: string | number): string => {
  if (!v) return '—';
  try {
    const d = typeof v === 'number' ? new Date(v) : new Date(`${v}T00:00:00`);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return String(v);
  }
};

const STATUS_META: Record<string, { label: string; cls: string }> = {
  invited: { label: 'Invited', cls: 'bg-blue-100 text-blue-800 border border-blue-200' },
  active: { label: 'Active', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
  suspended: { label: 'Suspended', cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
  terminated: { label: 'Terminated', cls: 'bg-red-100 text-red-800 border border-red-200' },
};

const REQUEST_STATUS_META: Record<string, { label: string; cls: string }> = {
  pending: { label: 'Pending', cls: 'bg-stone-100 text-stone-700 border border-stone-200' },
  submitted: { label: 'In review', cls: 'bg-amber-100 text-amber-800 border border-amber-200' },
  verified: { label: 'Verified', cls: 'bg-emerald-100 text-emerald-800 border border-emerald-200' },
  rejected: { label: 'Rejected', cls: 'bg-red-100 text-red-800 border border-red-200' },
};

const StatusBadge: React.FC<{ status?: string; map?: Record<string, { label: string; cls: string }> }> = ({
  status, map = STATUS_META,
}) => {
  const meta = (status && map[status]) || { label: status || 'unknown', cls: 'bg-stone-100 text-stone-700 border border-stone-200' };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.cls}`}>
      {meta.label}
    </span>
  );
};

const fileTypeIcon = (fileType?: string, fileName?: string) => {
  const t = `${fileType || ''} ${fileName || ''}`.toLowerCase();
  if (t.includes('image') || t.endsWith('.jpg') || t.endsWith('.jpeg') || t.endsWith('.png') || t.endsWith('.gif')) {
    return <Image size={18} className="text-sky-500" />;
  }
  if (t.includes('pdf')) return <File size={18} className="text-red-500" />;
  return <FileText size={18} className="text-stone-500" />;
};

const INPUT = 'w-full rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-900 placeholder-stone-400 focus:border-admin-primary focus:outline-none';
const BTN_GHOST = 'inline-flex items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 py-1.5 text-xs font-medium text-stone-700 transition-colors hover:bg-stone-50 disabled:opacity-50';

// Light modal shell shared by every dialog on this page (no native confirm/alert).
const Modal: React.FC<{
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  wide?: boolean;
}> = ({ title, onClose, children, footer, wide }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
    <div
      className={`flex max-h-[88vh] w-full ${wide ? 'max-w-lg' : 'max-w-md'} flex-col overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-xl`}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between border-b border-stone-200 px-5 py-3.5">
        <h3 className="text-sm font-semibold text-stone-900">{title}</h3>
        <button onClick={onClose} className="rounded-lg p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" aria-label="Close">
          <X size={16} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
      {footer && <div className="flex justify-end gap-2 border-t border-stone-200 bg-stone-50 px-5 py-3.5">{footer}</div>}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function StaffDetailPage() {
  const { staffId } = useParams<{ staffId: string }>();
  const navigate = useNavigate();

  // Staff record
  const [staff, setStaff] = useState<StaffMember | null>(null);
  const [role, setRole] = useState<StaffRole | null>(null);
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Document data
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [templates, setTemplates] = useState<any[]>([]);
  const [workflowTemplates, setWorkflowTemplates] = useState<any[]>([]);
  const [documents, setDocuments] = useState<StaffDocumentRecord[]>([]);
  const [requests, setRequests] = useState<StaffDocRequest[]>([]);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatusShape | null>(null);
  const [progressRefreshKey, setProgressRefreshKey] = useState(0);

  // Profile edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editHireDate, setEditHireDate] = useState('');

  // Role assignment
  const [pendingRoleId, setPendingRoleId] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState(false);

  // Status action modals
  const [pendingStatusChange, setPendingStatusChange] = useState<'active' | 'suspended' | null>(null);
  const [terminateOpen, setTerminateOpen] = useState(false);
  const [termReason, setTermReason] = useState('Voluntary resignation');
  const [termNotes, setTermNotes] = useState('');
  const [termNotify, setTermNotify] = useState(false);

  // Reset password / delete
  const [temporaryPassword, setTemporaryPassword] = useState('');
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Workflows
  const [assignOpen, setAssignOpen] = useState(false);
  const [workflowToRemove, setWorkflowToRemove] = useState<WorkflowSummary | null>(null);
  const [removingWorkflow, setRemovingWorkflow] = useState(false);

  // Reminder / re-request
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderMessage, setReminderMessage] = useState('');
  const [sendingReminder, setSendingReminder] = useState(false);
  const [reRequestTarget, setReRequestTarget] = useState<StaffDocRequest | null>(null);
  const [reRequestMessage, setReRequestMessage] = useState('');
  const [reRequestDueDate, setReRequestDueDate] = useState('');
  const [reRequesting, setReRequesting] = useState(false);

  // Admin document upload
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDocName, setUploadDocName] = useState('');
  const [uploadExpiry, setUploadExpiry] = useState('');
  const [uploading, setUploading] = useState(false);

  const displayName = staff?.name || staff?.email || 'Staff member';

  // ---------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------

  const loadStaff = useCallback(async () => {
    if (!staffId) return;
    try {
      const [res, rolesRes] = await Promise.all([
        staffApi.staff.get(staffId),
        staffApi.roles.list().catch(() => ({ roles: [] as StaffRole[], count: 0 })),
      ]);
      setStaff(res.staff);
      setRole(res.role);
      setRoles(rolesRes.roles || []);
      setLoadError(null);
    } catch (e: any) {
      setLoadError(e?.message || 'Failed to load staff member');
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  const loadDocumentData = useCallback(async () => {
    if (!staffId) return;
    setLoadingDocs(true);
    try {
      const [templatesData, workflowsData, docsData] = await Promise.all([
        staffDocumentTemplates.getAll().catch(() => []),
        staffOnboardingTemplates.getAll().catch(() => []),
        staffMemberDocuments.getByUser(staffId).catch(() => ({ documents: [], requests: [] })),
      ]);
      setTemplates(Array.isArray(templatesData) ? templatesData : []);
      setWorkflowTemplates(
        (Array.isArray(workflowsData) ? workflowsData : []).filter((w: any) => w?.isActive !== false),
      );
      setDocuments((docsData as any)?.documents || []);
      setRequests((docsData as any)?.requests || []);
      try {
        const status = await staffMemberDocuments.getOnboardingStatus(staffId);
        setOnboardingStatus(status as OnboardingStatusShape);
      } catch {
        setOnboardingStatus(null);
      }
    } finally {
      setLoadingDocs(false);
    }
  }, [staffId]);

  useEffect(() => {
    setLoading(true);
    void loadStaff();
    void loadDocumentData();
  }, [loadStaff, loadDocumentData]);

  const refreshDocs = () => {
    void loadDocumentData();
    setProgressRefreshKey((n) => n + 1);
  };

  // ---------------------------------------------------------------------
  // Profile / role / status actions
  // ---------------------------------------------------------------------

  const openEdit = () => {
    if (!staff) return;
    setEditName(staff.name || '');
    setEditTitle(staff.title || '');
    setEditPhone(staff.phone || '');
    setEditHireDate(staff.hireDate || '');
    setEditOpen(true);
  };

  const saveProfile = async () => {
    if (!staff) return;
    setBusy(true);
    try {
      const { staff: updated } = await staffApi.staff.update(staff.id, {
        name: editName.trim() || staff.name,
        title: editTitle.trim() || undefined,
        phone: editPhone.trim() || undefined,
        hireDate: editHireDate || undefined,
      });
      setStaff(updated);
      setEditOpen(false);
      toastService.success('Profile updated');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to update profile');
    } finally {
      setBusy(false);
    }
  };

  const saveRole = async () => {
    if (!staff || !pendingRoleId || pendingRoleId === staff.roleId) return;
    setSavingRole(true);
    try {
      const { staff: updated } = await staffApi.staff.update(staff.id, { roleId: pendingRoleId });
      setStaff(updated);
      setRole(roles.find((r) => r.id === pendingRoleId) || null);
      setPendingRoleId(null);
      toastService.success('Role updated');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to update role');
    } finally {
      setSavingRole(false);
    }
  };

  const applyStatus = async (newStatus: 'active' | 'suspended') => {
    if (!staff) return;
    setBusy(true);
    try {
      const { staff: updated } = await staffApi.staff.update(staff.id, { status: newStatus });
      setStaff(updated);
      toastService.success(newStatus === 'active' ? 'Staff member reactivated' : 'Staff member suspended');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to update status');
    } finally {
      setBusy(false);
      setPendingStatusChange(null);
    }
  };

  const confirmTerminate = async () => {
    if (!staff) return;
    setBusy(true);
    try {
      const { staff: updated } = await staffApi.staff.update(staff.id, {
        status: 'terminated' as StaffStatus,
        terminationReason: termReason,
        terminationNotes: termNotes.trim() || undefined,
        notifyEmployee: termNotify,
      });
      setStaff(updated);
      setTerminateOpen(false);
      toastService.success('Staff member terminated — access disabled.');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to terminate staff member');
    } finally {
      setBusy(false);
    }
  };

  const handleReactivate = async () => {
    if (!staff) return;
    setBusy(true);
    try {
      const { staff: updated } = await staffApi.staff.update(staff.id, { status: 'active' as StaffStatus });
      setStaff(updated);
      toastService.success('Staff member reactivated');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to reactivate');
    } finally {
      setBusy(false);
    }
  };

  const handleResetPassword = async () => {
    if (!staff) return;
    setBusy(true);
    try {
      const res = await staffApi.staff.resetPassword(staff.id);
      setTemporaryPassword(res.temporaryPassword || '');
      setPasswordModalOpen(true);
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to reset password');
    } finally {
      setBusy(false);
    }
  };

  const handleResendInvite = async () => {
    if (!staff) return;
    setBusy(true);
    try {
      await staffApi.staff.resendInvite(staff.id);
      toastService.success(`Invite resent to ${staff.email}`);
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to resend invite');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!staff || deleteConfirmText !== 'DELETE') return;
    setBusy(true);
    try {
      await staffApi.staff.remove(staff.id);
      toastService.success('Staff member deleted');
      navigate('/staff');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to delete staff member');
      setBusy(false);
      setDeleteOpen(false);
      setDeleteConfirmText('');
    }
  };

  // ---------------------------------------------------------------------
  // Workflows / documents actions
  // ---------------------------------------------------------------------

  const handleAssignWorkflow = async (workflowId: string) => {
    if (!staffId) return;
    // Thrown errors surface inside AssignWorkflowModal's own error state.
    await staffDocumentRequests.startOnboarding(staffId, workflowId);
    toastService.success('Workflow assigned — the staff member will be notified.');
    refreshDocs();
  };

  const handleRemoveWorkflow = async () => {
    if (!staffId) return;
    setRemovingWorkflow(true);
    try {
      const workflowId = workflowToRemove?.templateId || workflowToRemove?.id;
      await staffDocumentRequests.removeOnboarding(staffId, workflowId || undefined);
      toastService.success(workflowToRemove?.name ? `"${workflowToRemove.name}" workflow removed` : 'Workflow removed');
      setWorkflowToRemove(null);
      refreshDocs();
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to remove workflow');
    } finally {
      setRemovingWorkflow(false);
    }
  };

  const pendingRequests = useMemo(() => requests.filter((r) => r.status === 'pending'), [requests]);
  const completedRequests = useMemo(
    () => requests.filter((r) => r.status === 'verified' || r.status === 'rejected'),
    [requests],
  );

  const handleSendReminder = async () => {
    if (!staffId) return;
    setSendingReminder(true);
    try {
      await staffDocumentRequests.sendReminder(staffId, {
        requestIds: pendingRequests.map((r) => r.id),
        message: reminderMessage.trim() || undefined,
      });
      toastService.success('Reminder email sent');
      setReminderOpen(false);
      setReminderMessage('');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to send reminder');
    } finally {
      setSendingReminder(false);
    }
  };

  const handleReRequest = async () => {
    if (!staffId || !reRequestTarget) return;
    setReRequesting(true);
    try {
      await staffDocumentRequests.reRequest({
        userId: staffId,
        documentTemplateId: reRequestTarget.documentTemplateId,
        previousRequestId: reRequestTarget.id,
        message: reRequestMessage.trim() || undefined,
        dueDate: reRequestDueDate ? new Date(`${reRequestDueDate}T00:00:00`).getTime() : undefined,
      });
      toastService.success('Document re-requested — the staff member will be notified.');
      setReRequestTarget(null);
      setReRequestMessage('');
      setReRequestDueDate('');
      refreshDocs();
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to re-request document');
    } finally {
      setReRequesting(false);
    }
  };

  const handleAdminUpload = async () => {
    if (!staffId || !uploadFile) {
      toastService.error('Choose a file to upload');
      return;
    }
    setUploading(true);
    try {
      const contentType = uploadFile.type || 'application/octet-stream';
      const { uploadUrl, key } = await staffDocumentUploads.getUploadUrl(uploadFile.name, contentType, staffId);
      await staffDocumentUploads.uploadFile(uploadUrl, uploadFile, contentType);
      await staffMemberDocuments.addByAdmin(staffId, {
        fileName: uploadFile.name,
        fileKey: key,
        fileType: contentType,
        fileSize: uploadFile.size,
        documentName: uploadDocName.trim() || undefined,
        expiryDate: uploadExpiry ? new Date(`${uploadExpiry}T00:00:00`).getTime() : undefined,
      });
      toastService.success('Document uploaded');
      setUploadOpen(false);
      setUploadFile(null);
      setUploadDocName('');
      setUploadExpiry('');
      refreshDocs();
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleViewDocument = async (doc: StaffDocumentRecord) => {
    try {
      const { url } = await staffDocumentUploads.getViewUrl(doc.fileKey, doc.fileName);
      window.open(url, '_blank');
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to open document');
    }
  };

  const handleDownloadDocument = async (doc: StaffDocumentRecord) => {
    try {
      const { url } = await staffDocumentUploads.getDownloadUrl(doc.fileKey, doc.fileName);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e: any) {
      toastService.error(e?.message || 'Failed to download document');
    }
  };

  // ---------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------

  if (loading) {
    return (
      <StaffPage icon={UserIcon} title="Staff member" subtitle="Loading…">
        <div className="relative mx-auto max-w-[960px] space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-32 animate-pulse rounded-xl border border-stone-200 bg-white shadow-sm" />
          ))}
          <RubyLoader variant="overlay" />
        </div>
      </StaffPage>
    );
  }

  if (loadError || !staff) {
    return (
      <StaffPage icon={UserIcon} title="Staff member">
        <div className="mx-auto max-w-[960px] rounded-xl border border-stone-200 bg-white p-8 text-center shadow-sm">
          <AlertTriangle size={36} className="mx-auto mb-3 text-stone-400" />
          <p className="text-sm text-stone-600">{loadError || 'Staff member not found'}</p>
          <button onClick={() => navigate('/staff')} className={`${BTN_GHOST} mt-4`}>
            <ArrowLeft size={14} /> Back to Staff
          </button>
        </div>
      </StaffPage>
    );
  }

  // Nobody can be given the Owner role (the backend refuses it too).
  const roleOptions = roles
    .filter((r) => r.id !== 'role_owner' || r.id === staff?.roleId)
    .map((r) => ({ value: r.id, label: r.name, subtitle: r.description }));
  const ownerLocked = !!staff?.isOwner;
  const workflows = onboardingStatus?.workflows || [];

  return (
    <StaffPage
      icon={UserIcon}
      title={displayName}
      subtitle={`${role?.name ? `${role.name} · ` : ''}${staff.title || 'RubyTill internal staff member'}`}
      actions={(
        <>
          <button
            type="button"
            onClick={() => navigate('/staff')}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-white/10 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-white/20"
            title="Back to Staff"
          >
            <ArrowLeft size={16} />
            <span className="hidden sm:inline">Back</span>
          </button>
          <button
            type="button"
            onClick={() => setAssignOpen(true)}
            className="inline-flex min-h-[40px] items-center gap-2 rounded-lg bg-admin-primary px-4 py-2 text-sm font-medium text-stone-900 transition-colors hover:bg-admin-secondary"
          >
            <ClipboardList size={16} />
            <span className="hidden sm:inline">Assign Workflow</span>
          </button>
        </>
      )}
    >
      <div className="mx-auto max-w-[960px] space-y-4">

        {/* Termination banner — stays visible so the why/when is always on record. */}
        {staff.status === 'terminated' && (
          <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-5 py-3">
            <UserX size={18} className="mt-0.5 flex-shrink-0 text-red-500" />
            <div className="text-sm">
              <div className="font-bold text-red-800">Terminated — panel access disabled</div>
              <div className="mt-0.5 text-red-700">
                {staff.terminatedAt ? `Date: ${formatDate(staff.terminatedAt)}` : ''}
                {staff.terminationReason ? `  ·  Reason: ${staff.terminationReason}` : ''}
              </div>
              {staff.terminationNotes && <div className="mt-0.5 text-xs text-red-600">Note: {staff.terminationNotes}</div>}
            </div>
          </div>
        )}

        {/* ═══════ 1. PROFILE ═══════ */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <div className="flex items-center gap-2.5 border-b border-stone-200 px-6 py-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-100">
              <UserIcon size={16} className="text-stone-600" />
            </div>
            <h3 className="text-sm font-semibold text-stone-900">Profile</h3>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-red-600 to-red-900 text-xl font-bold text-white">
                {displayName.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex flex-wrap items-center gap-2">
                  <span className="text-lg font-semibold text-stone-900">{displayName}</span>
                  {role?.name && (
                    <span className="rounded-full bg-admin-primary/10 px-2 py-0.5 text-[11px] font-semibold text-admin-primary border border-admin-primary/30">
                      {role.name.toUpperCase()}
                    </span>
                  )}
                  <StatusBadge status={staff.status} />
                </div>
                <div className="flex flex-wrap items-center gap-4 text-sm text-stone-600">
                  <span className="flex items-center gap-1.5"><Mail size={14} />{staff.email}</span>
                  {staff.phone && <span className="flex items-center gap-1.5"><Phone size={14} />{staff.phone}</span>}
                  <span className="flex items-center gap-1.5" title="Hire date"><Calendar size={14} />{formatDate(staff.hireDate)}</span>
                </div>
              </div>
            </div>

            {/* Action buttons — the backend refuses self/protected-owner suspend,
                terminate and delete; those errors surface as toasts. The Owner's
                own row (R22) gets no actions at all. */}
            {ownerLocked ? (
              <p className="mt-4 border-t border-stone-200 pt-4 text-xs text-stone-500">
                This is the Owner account. It can't be edited, suspended, deleted or re-roled from the panel.
              </p>
            ) : (
            <div className="mt-4 flex flex-wrap gap-2 border-t border-stone-200 pt-4">
              <button onClick={openEdit} className={BTN_GHOST} title="Edit profile">
                <Edit2 size={14} /> Edit
              </button>
              <button onClick={handleResetPassword} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 transition-colors hover:bg-sky-100 disabled:opacity-50" title="Reset password">
                <Key size={14} /> Reset Password
              </button>
              {staff.status === 'invited' && (
                <button onClick={handleResendInvite} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-medium text-sky-700 transition-colors hover:bg-sky-100 disabled:opacity-50" title="Resend the invite email">
                  <Mail size={14} /> Resend Invite
                </button>
              )}
              {staff.status === 'terminated' ? (
                <button onClick={handleReactivate} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-50">
                  <UserCheck size={14} /> Reactivate
                </button>
              ) : (
                <>
                  {staff.status === 'active' && (
                    <button onClick={() => setPendingStatusChange('suspended')} className="inline-flex items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100">
                      <UserX size={14} /> Suspend
                    </button>
                  )}
                  {staff.status === 'suspended' && (
                    <button onClick={() => setPendingStatusChange('active')} className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 transition-colors hover:bg-emerald-100">
                      <UserCheck size={14} /> Reactivate
                    </button>
                  )}
                  <button
                    onClick={() => { setTermReason('Voluntary resignation'); setTermNotes(''); setTermNotify(false); setTerminateOpen(true); }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-red-700"
                    title="Terminate (disables all panel access)"
                  >
                    <UserX size={14} /> Terminate
                  </button>
                </>
              )}
              <button onClick={() => { setDeleteConfirmText(''); setDeleteOpen(true); }} className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-100" title="Delete staff member">
                <Trash2 size={14} /> Delete
              </button>
            </div>
            )}
          </div>
        </div>

        {/* ═══════ 2. PANEL ROLE ═══════ */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <div className="flex items-center gap-2.5 border-b border-stone-200 px-6 py-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-100">
              <Shield size={16} className="text-stone-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-stone-900">Panel Role</h3>
              <p className="text-xs text-stone-500">Which admin-panel areas this staff member can open</p>
            </div>
          </div>
          <div className="space-y-3 p-6">
            <div className="flex flex-wrap items-end gap-3">
              <div className="min-w-[240px] flex-1">
                <SearchableDropdown
                  label="Role"
                  options={roleOptions}
                  value={pendingRoleId ?? staff.roleId ?? null}
                  onChange={(v) => setPendingRoleId(v)}
                  disabled={ownerLocked}
                  placeholder="Select a role…"
                />
              </div>
              {pendingRoleId && pendingRoleId !== staff.roleId && (
                <div className="flex gap-2">
                  <button onClick={() => setPendingRoleId(null)} className={BTN_GHOST} disabled={savingRole}>Cancel</button>
                  <button
                    onClick={saveRole}
                    disabled={savingRole}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-admin-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-admin-secondary disabled:opacity-50"
                  >
                    {savingRole ? <RubyGemMark size={14} drawing color="currentColor" /> : <CheckCircle size={14} />}
                    {savingRole ? 'Saving…' : 'Save Role'}
                  </button>
                </div>
              )}
            </div>
            {role?.description && <p className="text-xs text-stone-500">{role.description}</p>}
            {role?.permissions && (
              <div className="flex flex-wrap gap-1.5">
                {/* permissions may be the legacy path list or the new { section: level } map */}
                {(Array.isArray(role.permissions)
                  ? (role.permissions as string[]).map((p) => [p, ''] as const)
                  : Object.entries(role.permissions as Record<string, string>)
                ).map(([p, lvl]) => (
                  <span key={p} className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] text-stone-700">{p === '*' ? 'Full access' : lvl ? `${p} · ${lvl}` : p}</span>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ═══════ 3. ASSIGNED WORKFLOWS ═══════ */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-2 border-b border-stone-200 px-6 py-4">
            <div className="flex items-center gap-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-100">
                <ClipboardList size={16} className="text-stone-600" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-stone-900">Onboarding Workflows</h3>
                <p className="text-xs text-stone-500">
                  {onboardingStatus?.totalDocuments
                    ? `${onboardingStatus.statusCounts?.verified || 0}/${onboardingStatus.totalDocuments} steps verified`
                    : 'Document workflows assigned to this staff member'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {pendingRequests.length > 0 && (
                <button onClick={() => setReminderOpen(true)} className={BTN_GHOST} title="Send reminder for pending documents">
                  <Bell size={12} /> Remind
                </button>
              )}
              <button onClick={() => setAssignOpen(true)} className={BTN_GHOST} title="Assign a workflow">
                <Plus size={12} /> Assign
              </button>
            </div>
          </div>

          {/* Workflow summary rows w/ remove — the per-step progress panel below. */}
          {workflows.length > 0 && (
            <div className="space-y-2 border-b border-stone-200 px-6 py-4">
              {workflows.map((wf, idx) => {
                const sc = wf.statusCounts || {};
                const total = (sc.verified || 0) + (sc.submitted || 0) + (sc.pending || 0) + (sc.rejected || 0);
                const isComplete = total > 0 && (sc.verified || 0) === total;
                const pct = total > 0 ? Math.round((((sc.verified || 0) + (sc.submitted || 0)) / total) * 100) : 0;
                return (
                  <div key={wf.templateId || wf.id || idx} className="flex items-center gap-3 rounded-lg border border-stone-200 bg-stone-50 p-3">
                    <div className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg ${isComplete ? 'bg-emerald-600' : 'bg-stone-200'}`}>
                      {isComplete ? <CheckCircle size={16} className="text-white" /> : <ClipboardList size={16} className="text-stone-500" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-1 flex items-center gap-2">
                        <span className="truncate text-sm font-medium text-stone-900">{wf.name}</span>
                        <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${isComplete ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-200 text-stone-600'}`}>
                          {sc.verified || 0}/{total}
                        </span>
                      </div>
                      <div className="h-1.5 w-full overflow-hidden rounded-full bg-stone-200">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${isComplete ? 'bg-emerald-500' : (sc.submitted || 0) > 0 ? 'bg-amber-400' : 'bg-stone-400'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                    <button
                      onClick={() => setWorkflowToRemove(wf)}
                      className="rounded p-1.5 text-stone-400 transition-colors hover:bg-stone-200 hover:text-red-600"
                      title="Remove workflow"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Per-step progress: verify / reject, submit-on-behalf, attach-result. */}
          <div className="p-2">
            <EmployeeProgressPanel
              tenantId="platform"
              employee={{ id: staff.id, name: displayName }}
              refreshKey={progressRefreshKey}
            />
          </div>
        </div>

        {/* ═══════ 4. DOCUMENTS ═══════ */}
        <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
          <div className="flex items-center gap-2.5 border-b border-stone-200 px-6 py-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-100">
              <FileText size={16} className="text-stone-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-stone-900">Documents {!loadingDocs && `(${documents.length})`}</h3>
              <p className="text-xs text-stone-500">Submitted and admin-uploaded files for this staff member</p>
            </div>
            {loadingDocs && <span className="inline-flex ml-2"><RubyGemMark size={14} drawing /></span>}
            <button
              onClick={() => setUploadOpen(true)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-admin-primary px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-admin-secondary"
              title="Upload a document for this staff member"
            >
              <Upload size={14} /> Upload
            </button>
          </div>
          {loadingDocs ? (
            <div className="relative space-y-3 p-6">
              <RubyLoader variant="overlay" size={32} />
              {[1, 2].map((i) => (
                <div key={i} className="flex animate-pulse items-center gap-4">
                  <div className="h-11 w-11 rounded-lg bg-stone-200" />
                  <div className="flex-1">
                    <div className="mb-2 h-4 w-40 rounded bg-stone-200" />
                    <div className="h-3 w-64 rounded bg-stone-100" />
                  </div>
                </div>
              ))}
            </div>
          ) : documents.length === 0 ? (
            <div className="p-8 text-center text-stone-500">
              <FileText size={36} className="mx-auto mb-3 text-stone-300" />
              <p className="text-sm">No documents yet</p>
            </div>
          ) : (
            <div className="divide-y divide-stone-200">
              {documents.map((doc) => {
                const req = requests.find((r) => r.id === doc.requestId);
                return (
                  <div key={doc.id} className="flex items-center gap-4 p-4 transition-colors hover:bg-stone-50">
                    <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg bg-stone-100">
                      {fileTypeIcon(doc.fileType, doc.fileName)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-stone-900">
                        {req?.templateName || doc.documentName || doc.fileName}
                      </p>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-stone-500">
                        <span className="max-w-[220px] truncate">{doc.fileName}</span>
                        {!!doc.fileSize && <span>{formatFileSize(doc.fileSize)}</span>}
                        {doc.uploadedAt && <span>Uploaded: {formatDate(doc.uploadedAt)}</span>}
                        {doc.expiryDate && (
                          <span className={doc.expiryDate < Date.now() + 30 * 24 * 60 * 60 * 1000 ? 'font-medium text-amber-600' : ''}>
                            Expires: {formatDate(doc.expiryDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    {req && <StatusBadge status={req.status} map={REQUEST_STATUS_META} />}
                    <div className="flex items-center gap-1">
                      <button onClick={() => handleViewDocument(doc)} className="rounded-lg p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" title="View">
                        <Eye size={16} />
                      </button>
                      <button onClick={() => handleDownloadDocument(doc)} className="rounded-lg p-2 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" title="Download">
                        <Download size={16} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* ═══════ 5. REQUEST HISTORY ═══════ */}
        {!loadingDocs && completedRequests.length > 0 && (
          <div className="overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm">
            <div className="flex items-center gap-2.5 border-b border-stone-200 px-6 py-4">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-stone-100">
                <Clock size={16} className="text-stone-600" />
              </div>
              <h3 className="text-sm font-semibold text-stone-900">Request History</h3>
            </div>
            <div className="divide-y divide-stone-200">
              {completedRequests.map((req) => {
                const doc = documents.find((d) => d.requestId === req.id);
                return (
                  <div key={req.id} className="flex items-center gap-4 p-4">
                    <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${req.status === 'verified' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                      {req.status === 'verified'
                        ? <CheckCircle size={18} className="text-emerald-600" />
                        : <XCircle size={18} className="text-red-500" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-stone-900">{req.templateName}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-stone-500">
                        <span>{req.status === 'verified' ? 'Verified' : 'Rejected'}</span>
                        {req.verifiedAt && <span>{formatDate(req.verifiedAt)}</span>}
                        {req.rejectionReason && <span className="text-red-600">Reason: {req.rejectionReason}</span>}
                      </div>
                    </div>
                    <StatusBadge status={req.status} map={REQUEST_STATUS_META} />
                    {req.status === 'rejected' && (
                      <button
                        onClick={() => { setReRequestTarget(req); setReRequestMessage(''); setReRequestDueDate(''); }}
                        className="inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700 transition-colors hover:bg-amber-100"
                      >
                        <RotateCcw size={12} /> Re-request
                      </button>
                    )}
                    {doc && (
                      <button onClick={() => handleDownloadDocument(doc)} className="rounded p-1.5 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-700" title="Download">
                        <Download size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ═══════ MODALS ═══════ */}

      {/* Edit profile */}
      {editOpen && (
        <Modal
          title="Edit Profile"
          onClose={() => setEditOpen(false)}
          footer={(
            <>
              <button onClick={() => setEditOpen(false)} className={BTN_GHOST} disabled={busy}>Cancel</button>
              <button
                onClick={saveProfile}
                disabled={busy || !editName.trim()}
                className="inline-flex items-center gap-1.5 rounded-lg bg-admin-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-admin-secondary disabled:opacity-50"
              >
                {busy ? 'Saving…' : 'Save Changes'}
              </button>
            </>
          )}
        >
          <div className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Name</label>
              <input type="text" value={editName} onChange={(e) => setEditName(e.target.value)} className={INPUT} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Title</label>
              <input type="text" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} placeholder="e.g. Support Engineer" className={INPUT} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Phone</label>
              <input type="tel" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} placeholder="+1 …" className={INPUT} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Hire date</label>
              <DatePicker value={editHireDate} onChange={setEditHireDate} placeholder="Select hire date" />
            </div>
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
              <p className="text-sm text-stone-700"><span className="font-medium">Email:</span> {staff.email}</p>
              <p className="mt-1 text-xs text-stone-500">Email address cannot be changed.</p>
            </div>
          </div>
        </Modal>
      )}

      {/* Suspend / reactivate confirmation */}
      {pendingStatusChange && (
        <Modal
          title={pendingStatusChange === 'suspended' ? 'Suspend Staff Member' : 'Reactivate Staff Member'}
          onClose={() => setPendingStatusChange(null)}
          footer={(
            <>
              <button onClick={() => setPendingStatusChange(null)} className={BTN_GHOST}>Cancel</button>
              <button
                onClick={() => applyStatus(pendingStatusChange)}
                disabled={busy}
                className={`rounded-lg px-4 py-2 text-xs font-semibold text-white transition-colors disabled:opacity-50 ${
                  pendingStatusChange === 'suspended' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                {busy
                  ? (pendingStatusChange === 'suspended' ? 'Suspending…' : 'Reactivating…')
                  : (pendingStatusChange === 'suspended' ? 'Suspend' : 'Reactivate')}
              </button>
            </>
          )}
        >
          <div className="flex items-start gap-3">
            <div className={`rounded-full p-2 ${pendingStatusChange === 'suspended' ? 'bg-amber-100' : 'bg-emerald-100'}`}>
              {pendingStatusChange === 'suspended'
                ? <UserX size={22} className="text-amber-600" />
                : <UserCheck size={22} className="text-emerald-600" />}
            </div>
            <div>
              <p className="text-sm font-medium text-stone-900">
                {pendingStatusChange === 'suspended'
                  ? 'Suspend this staff member?'
                  : 'Reactivate this staff member?'}
              </p>
              <p className="mt-1 text-sm text-stone-600">
                {pendingStatusChange === 'suspended' ? (
                  <>This revokes <strong className="text-stone-900">{displayName}</strong> ({staff.email})&rsquo;s admin-panel access. You can reactivate them at any time.</>
                ) : (
                  <>This restores <strong className="text-stone-900">{displayName}</strong> ({staff.email})&rsquo;s admin-panel access.</>
                )}
              </p>
            </div>
          </div>
        </Modal>
      )}

      {/* Terminate */}
      {terminateOpen && (
        <Modal
          title={<span className="flex items-center gap-2 text-red-600"><UserX size={16} /> Terminate staff member</span>}
          onClose={() => !busy && setTerminateOpen(false)}
          footer={(
            <>
              <button onClick={() => setTerminateOpen(false)} disabled={busy} className={BTN_GHOST}>Cancel</button>
              <button
                onClick={confirmTerminate}
                disabled={busy}
                className="rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? 'Terminating…' : 'Terminate & deactivate'}
              </button>
            </>
          )}
        >
          <div className="space-y-3">
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              This will <b>terminate and deactivate</b> <b>{displayName}</b> — all admin-panel access is revoked immediately.
              You can reactivate later if this was a mistake.
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-600">Reason</label>
              <div className="mt-1">
                <SearchableDropdown
                  options={[
                    { value: 'Voluntary resignation', label: 'Voluntary resignation' },
                    { value: 'Performance', label: 'Performance' },
                    { value: 'Position eliminated', label: 'Position eliminated' },
                    { value: 'Misconduct', label: 'Misconduct' },
                    { value: 'Other', label: 'Other' },
                  ]}
                  value={termReason}
                  onChange={(v) => setTermReason(v || 'Voluntary resignation')}
                  placeholder="Select reason…"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-600">Notes (optional)</label>
              <textarea value={termNotes} onChange={(e) => setTermNotes(e.target.value)} rows={2} placeholder="Internal note…" className={`${INPUT} mt-1 resize-none`} />
            </div>
            <label className="flex items-center gap-2 text-sm text-stone-700">
              <input type="checkbox" checked={termNotify} onChange={(e) => setTermNotify(e.target.checked)} className="h-4 w-4" />
              Send notification email to the staff member
            </label>
          </div>
        </Modal>
      )}

      {/* Reset password result */}
      {passwordModalOpen && (
        <Modal
          title="Password Reset"
          onClose={() => setPasswordModalOpen(false)}
          footer={(
            <button onClick={() => setPasswordModalOpen(false)} className="rounded-lg bg-admin-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-admin-secondary">
              Done
            </button>
          )}
        >
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
            <div className="mb-2 flex items-center gap-2 text-emerald-700">
              <CheckCircle size={18} />
              <span className="text-sm font-medium">Password reset successfully</span>
            </div>
            <p className="mb-3 text-sm text-emerald-700">
              The temporary password below has also been emailed to {staff.email}. They&rsquo;ll be
              prompted to change it on next sign-in.
            </p>
            <div className="flex items-center justify-between gap-2 rounded-lg border border-emerald-200 bg-white p-3">
              <code className="break-all font-mono text-base text-stone-900">{temporaryPassword || '—'}</code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(temporaryPassword);
                  toastService.success('Password copied');
                }}
                className="inline-flex flex-shrink-0 items-center gap-1.5 rounded bg-admin-primary px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-admin-secondary"
              >
                <Copy size={12} /> Copy
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete — typed confirmation */}
      {deleteOpen && (
        <Modal
          title={<span className="text-red-600">Delete Staff Member</span>}
          onClose={() => { setDeleteOpen(false); setDeleteConfirmText(''); }}
          footer={(
            <>
              <button onClick={() => { setDeleteOpen(false); setDeleteConfirmText(''); }} className={BTN_GHOST}>Cancel</button>
              <button
                onClick={handleDelete}
                disabled={busy || deleteConfirmText !== 'DELETE'}
                className="rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </>
          )}
        >
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-red-100 p-2">
                <AlertTriangle size={22} className="text-red-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-stone-900">Permanently delete this staff member?</p>
                <p className="mt-1 text-sm text-stone-600">
                  This removes <strong className="text-stone-900">{displayName}</strong> ({staff.email}) and
                  their panel account. This action cannot be undone.
                </p>
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">
                Type <span className="font-mono text-red-600">DELETE</span> to confirm
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                autoFocus
                className={INPUT}
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Assign workflow */}
      {assignOpen && (
        <AssignWorkflowModal
          tenantId="platform"
          employee={{ id: staff.id, name: displayName }}
          workflows={workflowTemplates}
          templates={templates}
          onAssign={handleAssignWorkflow}
          onClose={() => setAssignOpen(false)}
        />
      )}

      {/* Remove workflow confirmation */}
      {workflowToRemove && (
        <Modal
          title={<span className="text-red-600">Remove Workflow</span>}
          onClose={() => setWorkflowToRemove(null)}
          footer={(
            <>
              <button onClick={() => setWorkflowToRemove(null)} className={BTN_GHOST}>Cancel</button>
              <button
                onClick={handleRemoveWorkflow}
                disabled={removingWorkflow}
                className="rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {removingWorkflow ? 'Removing…' : 'Remove Workflow'}
              </button>
            </>
          )}
        >
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-red-100 p-2">
              <AlertTriangle size={22} className="text-red-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-stone-900">
                Remove &ldquo;{workflowToRemove.name}&rdquo; from {displayName}?
              </p>
              <p className="mt-1 text-sm text-stone-600">
                All pending document requests for this workflow will be cancelled.
              </p>
              {(workflowToRemove.statusCounts?.submitted || 0) > 0 && (
                <p className="mt-2 text-sm text-amber-700">
                  Note: {workflowToRemove.statusCounts?.submitted} submitted document(s) are awaiting review.
                </p>
              )}
            </div>
          </div>
        </Modal>
      )}

      {/* Send reminder */}
      {reminderOpen && (
        <Modal
          title={<span className="flex items-center gap-2"><Bell size={16} className="text-amber-500" /> Send Reminder</span>}
          onClose={() => setReminderOpen(false)}
          footer={(
            <>
              <button onClick={() => setReminderOpen(false)} className={BTN_GHOST}>Cancel</button>
              <button
                onClick={handleSendReminder}
                disabled={sendingReminder}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
              >
                {sendingReminder ? <RubyGemMark size={14} drawing color="currentColor" /> : <Send size={14} />}
                {sendingReminder ? 'Sending…' : 'Send Reminder'}
              </button>
            </>
          )}
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
              <p className="text-sm text-amber-800">
                This emails <strong>{displayName}</strong> about their {pendingRequests.length} pending
                document{pendingRequests.length !== 1 ? 's' : ''}.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Custom message (optional)</label>
              <textarea
                value={reminderMessage}
                onChange={(e) => setReminderMessage(e.target.value)}
                placeholder="Add a personal message to the reminder email…"
                className={`${INPUT} h-24 resize-none`}
              />
            </div>
            {pendingRequests.length > 0 && (
              <div className="rounded-lg border border-stone-200 bg-stone-50 p-3">
                <p className="mb-2 text-xs font-medium text-stone-500">Pending documents:</p>
                <ul className="space-y-1">
                  {pendingRequests.map((req) => (
                    <li key={req.id} className="flex items-center gap-2 text-sm text-stone-700">
                      <Clock size={12} className="text-amber-500" />
                      {req.templateName}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Modal>
      )}

      {/* Re-request document */}
      {reRequestTarget && (
        <Modal
          title={<span className="flex items-center gap-2"><RotateCcw size={16} className="text-amber-500" /> Re-request Document</span>}
          onClose={() => setReRequestTarget(null)}
          footer={(
            <>
              <button onClick={() => setReRequestTarget(null)} className={BTN_GHOST}>Cancel</button>
              <button
                onClick={handleReRequest}
                disabled={reRequesting}
                className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
              >
                {reRequesting ? <RubyGemMark size={14} drawing color="currentColor" /> : <Send size={14} />}
                {reRequesting ? 'Requesting…' : 'Send Request'}
              </button>
            </>
          )}
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
              <p className="text-sm text-sky-800">
                Re-requesting <strong>{reRequestTarget.templateName}</strong> from {displayName}.
                They will receive an email notification.
              </p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Message to staff member</label>
              <textarea
                value={reRequestMessage}
                onChange={(e) => setReRequestMessage(e.target.value)}
                placeholder="Explain why you're requesting this document again…"
                className={`${INPUT} h-24 resize-none`}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">New due date (optional)</label>
              <DatePicker
                value={reRequestDueDate}
                onChange={setReRequestDueDate}
                min={new Date().toISOString().split('T')[0]}
                placeholder="Select due date"
              />
            </div>
          </div>
        </Modal>
      )}

      {/* Admin upload */}
      {uploadOpen && (
        <Modal
          title="Upload Document"
          onClose={() => { setUploadOpen(false); setUploadFile(null); setUploadDocName(''); setUploadExpiry(''); }}
          footer={(
            <>
              <button onClick={() => { setUploadOpen(false); setUploadFile(null); setUploadDocName(''); setUploadExpiry(''); }} className={BTN_GHOST}>Cancel</button>
              <button
                onClick={handleAdminUpload}
                disabled={uploading || !uploadFile}
                className="inline-flex items-center gap-1.5 rounded-lg bg-admin-primary px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-admin-secondary disabled:opacity-50"
              >
                {uploading ? <RubyGemMark size={14} drawing color="currentColor" /> : <Upload size={14} />}
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </>
          )}
        >
          <div className="space-y-4">
            <p className="text-xs text-stone-500">Attach a document directly to {displayName}&rsquo;s record.</p>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Document name (optional)</label>
              <input
                type="text"
                value={uploadDocName}
                onChange={(e) => setUploadDocName(e.target.value)}
                placeholder="e.g. Signed Contract"
                className={INPUT}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">File *</label>
              <input
                type="file"
                onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                className={`${INPUT} file:mr-3 file:rounded file:border-0 file:bg-stone-100 file:px-3 file:py-1 file:text-xs file:text-stone-700`}
              />
              {uploadFile && (
                <p className="mt-1 text-xs text-stone-500">{uploadFile.name} · {formatFileSize(uploadFile.size)}</p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold text-stone-600">Expiry date (optional)</label>
              <DatePicker
                value={uploadExpiry}
                onChange={setUploadExpiry}
                min={new Date().toISOString().split('T')[0]}
                placeholder="Select expiry date"
              />
            </div>
          </div>
        </Modal>
      )}
    </StaffPage>
  );
}
